import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Module-level callback registry — allows emitToast() to be called from outside React
// (e.g., from the axios interceptor) and still reach the ToastProvider state.
let _addToastCallback = null;

/**
 * Call this from anywhere (including axios interceptors) to show a toast.
 * @param {{ type: 'error'|'success'|'warning'|'info', message: string, detail?: { status?: number|null, raw?: string|null, correlationId?: string|null } }} toast
 */
// eslint-disable-next-line react-refresh/only-export-components
export function emitToast(toast) {
  if (_addToastCallback) {
    _addToastCallback(toast);
  }
}

const ToastContext = createContext(null);

let _nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(({ type = 'info', message, detail }) => {
    const id = _nextId++;
    setToasts((prev) => [...prev, { id, type, message, detail }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  // Register the addToast callback so emitToast() can reach it from outside React
  useEffect(() => {
    _addToastCallback = addToast;
    return () => {
      _addToastCallback = null;
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => useContext(ToastContext);
